import * as React from "react";
import { cn } from "@repo/utils/cn";
import Image from "next/image";
import { Link } from "./Link";
import { LanguageSelector } from "./LanguageSelector";

export interface FooterLink {
  text: string;
  href: string;
  target?: string;
  noFollow?: boolean;
  onClick?: () => void;
}

export interface FooterLinkGroup {
  mainLink?: FooterLink | null;
  links?: FooterLink[];
}

export interface SocialLink {
  icon: string;
  href: string;
  alt: string;
  target?: string;
  noFollow?: boolean;
}

export interface FooterProps {
  logo?: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
  };
  languageSelector?: {
    currentLanguage: string;
    onLanguageChange?: () => void;
  };
  linkGroups?: FooterLinkGroup[];
  socialLinks?: {
    title?: string;
    links: SocialLink[];
  };
  legalLinks?: FooterLink[];
  copyrightText?: string;
  className?: string;
}

export const Footer: React.FC<FooterProps> = ({
  logo,
  languageSelector,
  linkGroups,
  socialLinks,
  legalLinks,
  copyrightText,
  className,
}) => {
  return (
    <footer
      className={cn(
        "uipm:bg-[var(--typography-colors-label-inverted)] uipm:text-[var(--typography-colors-text-inverted)] uipm:w-full",
        "uipm:px-[var(--padding-padding-default)] uipm:py-8",
        "uipm:md:px-[var(--padding-padding-default)] uipm:md:py-12",
        "uipm:lg:px-6 uipm:lg:py-14",
        className,
      )}
    >
      <div className="uipm:max-w-[1440px] uipm:mx-auto">
        <div
          className={cn(
            "uipm:flex uipm:flex-col uipm:gap-8",
            "uipm:mb-[var(--margin-margin-default)]",
            "uipm:md:gap-8 uipm:md:mb-[var(--margin-margin-default)]",
            "uipm:lg:mb-14 uipm:lg:gap-14",
          )}
        >
          <div
            className={cn(
              "uipm:flex uipm:items-center uipm:gap-6",
              "uipm:md:justify-between",
              "uipm:lg:hidden",
            )}
          >
            <div className="uipm:flex uipm:items-center uipm:gap-6 uipm:w-full uipm:md:w-auto">
              {logo && (
                <div className="uipm:w-12 uipm:h-12 uipm:flex-shrink-0">
                  <Image
                    src={logo.src}
                    alt={logo.alt}
                    width={logo.width || 48}
                    height={logo.height || 48}
                    className="uipm:object-contain"
                  />
                </div>
              )}

              {/* {languageSelector && (
                <LanguageSelector
                  currentLanguage={languageSelector.currentLanguage}
                  onLanguageChange={languageSelector.onLanguageChange}
                  variant="footer-mobile"
                />
              )} */}
            </div>

            {socialLinks && socialLinks.links.length > 0 && (
              <div className="uipm:hidden uipm:md:flex uipm:md:flex-col uipm:md:gap-2 uipm:md:items-end">
                {socialLinks.title && (
                  <p className="uipm:text-[var(--typography-colors-text-inverted)] uipm:font-medium uipm:text-(length:--typography-sizes-body-medium) uipm:leading-[var(--typography-line-heights-body-medium)] uipm:m-0">
                    {socialLinks.title}
                  </p>
                )}
                <div className="uipm:flex uipm:gap-4">
                  {socialLinks.links.map((social, index) => (
                    <Link
                      key={index}
                      href={social.href}
                      target={social.target || "_blank"}
                      noFollow={social.noFollow}
                      aria-label={social.alt}
                      className="uipm:block uipm:w-8 uipm:h-8 hover:uipm:opacity-80 uipm:transition-opacity"
                    >
                      <Image
                        src={social.icon}
                        alt={social.alt}
                        width={32}
                        height={32}
                        className="uipm:w-full uipm:h-full uipm:object-contain"
                      />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div
            className={cn(
              "uipm:flex uipm:flex-col uipm:gap-8",
              "uipm:md:flex-row uipm:md:justify-between",
              "uipm:lg:flex-row uipm:lg:gap-6",
            )}
          >
            <div
              className={cn(
                "uipm:hidden",
                "uipm:lg:flex uipm:lg:flex-col uipm:lg:justify-between uipm:lg:gap-8 uipm:lg:min-w-[200px]",
              )}
            >
              <div className="uipm:flex uipm:items-center uipm:gap-4">
                {logo && (
                  <div className="uipm:w-12 uipm:h-12 uipm:flex-shrink-0">
                    <Image
                      src={logo.src}
                      alt={logo.alt}
                      width={logo.width || 48}
                      height={logo.height || 48}
                      className="uipm:object-contain"
                    />
                  </div>
                )}

                {/* {languageSelector && (
                  <LanguageSelector
                    currentLanguage={languageSelector.currentLanguage}
                    onLanguageChange={languageSelector.onLanguageChange}
                    variant="footer-desktop"
                  />
                )} */}
              </div>

              {socialLinks && socialLinks.links.length > 0 && (
                <div className="uipm:flex uipm:flex-col uipm:gap-2">
                  {socialLinks.title && (
                    <p className="uipm:text-[var(--typography-colors-text-inverted)] uipm:font-medium uipm:text-(length:--typography-sizes-body-medium) uipm:leading-[var(--typography-line-heights-body-medium)] uipm:m-0">
                      {socialLinks.title}
                    </p>
                  )}
                  <div className="uipm:flex uipm:gap-6">
                    {socialLinks.links.map((social, index) => (
                      <Link
                        key={index}
                        href={social.href}
                        target={social.target || "_blank"}
                        noFollow={social.noFollow}
                        aria-label={social.alt}
                        className="uipm:block uipm:w-8 uipm:h-8 hover:uipm:opacity-80 uipm:transition-opacity"
                      >
                        <Image
                          src={social.icon}
                          alt={social.alt}
                          width={32}
                          height={32}
                          className="uipm:w-full uipm:h-full uipm:object-contain"
                        />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {linkGroups && linkGroups.length > 0 && (
              <div
                className={cn(
                  "uipm:grid uipm:grid-cols-2 uipm:gap-y-8 uipm:gap-x-4",
                  "uipm:font-[var(--font-family-sans)]",
                  "uipm:md:flex uipm:md:flex-wrap uipm:md:gap-4 uipm:md:flex-1",
                  "uipm:lg:grid uipm:lg:grid-cols-4 uipm:lg:gap-6 uipm:lg:flex-1",
                )}
              >
                {linkGroups.map((group, index) => (
                  <div
                    key={index}
                    className={cn(
                      "uipm:flex uipm:flex-col uipm:gap-2",
                      "uipm:md:flex-1 uipm:md:min-w-[160px]",
                    )}
                  >
                    {group.mainLink && (
                      <p className="uipm:text-[var(--color-color-accent-secondary-focus)] uipm:font-semibold uipm:text-(length:--typography-sizes-body-medium) uipm:leading-[var(--typography-line-heights-body-medium)] uipm:mb-2 uipm:mt-0">
                        {group.mainLink.text}
                      </p>
                    )}
                    {group.links && group.links.length > 0 && (
                      <ul className="uipm:flex uipm:flex-col uipm:gap-2 uipm:list-none uipm:p-0 uipm:m-0">
                        {group.links.map((link, linkIndex) => (
                          <li key={linkIndex}>
                            <Link
                              href={link.href}
                              target={link.target}
                              noFollow={link.noFollow}
                              onClick={link.onClick}
                              className="uipm:text-[var(--typography-colors-text-inverted-faded)] uipm:font-medium uipm:text-(length:--typography-sizes-body-medium) uipm:leading-[var(--typography-line-heights-body-medium)] uipm:no-underline uipm:hover:underline uipm:transition-all"
                            >
                              {link.text}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {socialLinks && socialLinks.links.length > 0 && (
            <div
              className={cn(
                "uipm:flex uipm:flex-col uipm:gap-2",
                "uipm:md:hidden",
              )}
            >
              {socialLinks.title && (
                <p className="uipm:text-[var(--typography-colors-text-inverted)] uipm:font-medium uipm:text-(length:--typography-sizes-body-medium) uipm:leading-[var(--typography-line-heights-body-medium)] uipm:m-0">
                  {socialLinks.title}
                </p>
              )}
              <div className="uipm:flex uipm:gap-4">
                {socialLinks.links.map((social, index) => (
                  <Link
                    key={index}
                    href={social.href}
                    target={social.target || "_blank"}
                    noFollow={social.noFollow}
                    className="uipm:block uipm:w-8 uipm:h-8 hover:uipm:opacity-80 uipm:transition-opacity"
                  >
                    <Image
                      src={social.icon}
                      alt={social.alt}
                      width={32}
                      height={32}
                      className="uipm:w-full uipm:h-full uipm:object-contain"
                    />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          className={cn(
            "uipm:border-t uipm:border-solid uipm:border-[rgba(255,255,255,0.5)] uipm:pt-[var(--padding-padding-default)]",
            "uipm:md:pt-[var(--padding-padding-default)]",
            "uipm:lg:pt-6 uipm:lg:border-[var(--color-color-accent-secondary-hover)]",
            "uipm:flex uipm:flex-col uipm:gap-2",
            "uipm:md:flex-row uipm:md:justify-between uipm:md:items-center uipm:md:gap-0",
          )}
        >
          {legalLinks && legalLinks.length > 0 && (
            <div
              className={cn(
                "uipm:flex uipm:flex-col uipm:gap-2",
                "uipm:md:flex-row uipm:md:gap-4",
                "uipm:lg:gap-6",
              )}
            >
              {legalLinks.map((link, index) => (
                <Link
                  key={index}
                  href={link.href}
                  target={link.target}
                  noFollow={link.noFollow}
                  onClick={link.onClick}
                  className="uipm:text-[var(--typography-colors-text-inverted-faded)] uipm:font-normal uipm:text-xs uipm:leading-[var(--typography-line-heights-body-xs)] uipm:no-underline uipm:hover:underline uipm:transition-all"
                >
                  {link.text}
                </Link>
              ))}
            </div>
          )}

          {copyrightText && (
            <p className="uipm:text-[var(--color-color-accent-secondary-hover)] uipm:font-normal uipm:text-xs uipm:leading-[var(--typography-line-heights-body-xs)] uipm:m-0">
              {copyrightText}
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};
